import { getTask, type Task } from './tasks';

/**
 * The task details this tab has already seen, kept between panel opens.
 *
 * The panel is a route, so it is torn down and rebuilt on every open, on every
 * subtask link followed and on every Back. Without somewhere to keep the last
 * answer, each of those is a fresh round trip in front of an empty panel --
 * even for a task whose details were on screen a second earlier.
 *
 * It holds `Task` and never `TaskSummary`. A summary written in here would look
 * like a cache hit while silently missing the five relation keys that are the
 * only reason the detail request exists.
 *
 * Nothing here expires by time. Every mutation in the app answers with the full
 * task and writes it back through `putTask`, so an entry goes stale only when
 * someone edits the same task in another tab -- and `fetchTask` revalidates on
 * every open anyway.
 */

/**
 * Enough to cover a session's worth of moving around a project without letting
 * a long-lived tab grow a copy of every task in the workspace.
 */
const MAX_ENTRIES = 50;

const cache = new Map<string, Task>();

/**
 * Requests that have been made but not answered.
 *
 * Two callers can ask for the same task in the same tick -- the panel mounting
 * while an earlier open is still in flight -- and without this they would each
 * pay for their own round trip.
 */
const inFlight = new Map<string, Promise<Task>>();

export const getCachedTask = (taskId: string): Task | undefined => cache.get(taskId);

export function putTask(task: Task): void {
  // Deleted before it is set so the key moves to the end: `Map` iterates in
  // insertion order, which turns the eviction below into least-recently-written
  // rather than oldest-ever-written.
  cache.delete(task.id);
  cache.set(task.id, task);

  if (cache.size > MAX_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
}

/** For a task that is gone: deleted here, or answered for with a 404. */
export function evictTask(taskId: string): void {
  cache.delete(taskId);
}

/**
 * The task's details, from the network, shared with anyone else asking at the
 * same moment.
 *
 * Deliberately takes no `AbortSignal`. The request is shared, so a caller
 * unmounting must not cancel it out from under the others still waiting -- and
 * the answer is worth keeping for the cache regardless of who asked. Callers
 * that no longer want the result ignore it instead of aborting it.
 */
export function fetchTask(taskId: string): Promise<Task> {
  const existing = inFlight.get(taskId);
  if (existing) return existing;

  const request = getTask(taskId)
    .then(({ task }) => {
      putTask(task);
      return task;
    })
    .finally(() => {
      inFlight.delete(taskId);
    });

  inFlight.set(taskId, request);
  return request;
}
